import type {
  BriqpayCartItem,
  BriqpayCreateSessionRequest,
  BriqpayCustomerType,
  BriqpayHook,
  BriqpayModuleSettings,
  BriqpayModulesConfig,
  BriqpaySession,
  CountryCode,
  CreateOrderItemDTO,
  CreateOrderRequest,
  CreateOrderResponse,
  Language,
  OrderDTO
} from 'ecosystem'
import { CustomerType } from 'ecosystem'
import { briqpayApi, parseStoreToBackendOrderLines, paymentShippingFactory } from 'api'
import {
  DEFAULT_COUNTRY,
  DEFAULT_CURRENCY,
  getFullLocale,
  IS_PRODUCTION,
  resolveUrlPath
} from 'shared-utils'
import type { NextApiRequest, NextApiResponse } from 'next'
import storefrontApi from 'api/storefront-api'
import {
  briqpayOrderFactory,
  briqpayOrderLineFactory,
  briqpayShippingLine
} from 'api/utils/briqpay/checkout'
import { type CreateOrderHandlerOpts } from './types'
import { ingridShippingProvider } from './ingridShippingProvider'
import { nshiftShippingProvider } from './nshiftShippingProvider'

/** Briqpay wants `"sv-se"` / `"en-gb"`, not a bare language code. */
const getBriqpayLocale = (locale: Language) =>
  getFullLocale(locale, {
    no: 'nb-NO',
    en: 'en-GB'
  }).toLowerCase()

const resolveBriqpayCountry = (
  opts: CreateOrderHandlerOpts,
  requestedCountryCode: CountryCode | undefined
): CountryCode | undefined => {
  const briqpayOptions = opts.paymentProviderConfig?.providerOptions?.briqpay
  const { availableCountries, countryCode: configCountryCode } = briqpayOptions ?? {}

  if (
    requestedCountryCode &&
    (!availableCountries?.length || availableCountries.includes(requestedCountryCode))
  ) {
    return requestedCountryCode
  }

  return configCountryCode
}

/**
 * Business customers get Briqpay's B2B flow — organisation-number lookup and the invoice
 * and leasing PSPs that only accept companies. Everyone else gets the consumer flow.
 * Fixed at create: a PATCH cannot change it.
 */
const resolveBriqpayCustomerType = (opts: CreateOrderHandlerOpts): BriqpayCustomerType =>
  opts.paymentProviderConfig?.providerOptions?.briqpay?.customerType ??
  (opts.customerType === CustomerType.BUSINESS ? 'business' : 'consumer')

/**
 * Build `modules` from the store settings.
 *
 * The module layout only varies for business sessions — Briqpay renders consumer sessions
 * payment-only regardless — so `mod` is supplied for B2B and omitted otherwise. Decisions
 * is the exception: it configures the payment module, which every session renders, so it
 * is applied for both customer types.
 *
 * Only emit keys that are actually set: `modules` is a strict schema and an unknown or
 * misplaced field is rejected with a 400 naming its exact path.
 */
const briqpayModulesFactory = (
  mod: BriqpayModuleSettings | undefined,
  decisionEnabled: boolean | undefined
): BriqpayModulesConfig => {
  const config: NonNullable<BriqpayModulesConfig['config']> = {}

  if (decisionEnabled) {
    config.payment = { decision: { enabled: true } }
  }
  if (mod?.companyPrefillLock) {
    config.company_lookup = { companyPrefillLock: mod.companyPrefillLock }
  }
  if (mod?.billingLockFields) {
    config.billing = { lockFields: mod.billingLockFields }
  }
  if (mod?.shippingLockFields) {
    config.shipping = { lockFields: mod.shippingLockFields }
  }
  if (mod?.termsCheckboxes?.length) {
    config.terms = { checkboxes: mod.termsCheckboxes }
  }
  if (mod?.orderNote) {
    config.order_note = mod.orderNote
  }
  if (mod?.upsell) {
    config.upsell = mod.upsell
  }

  return {
    ...(mod?.loadModules?.length ? { loadModules: mod.loadModules } : {}),
    ...(Object.keys(config).length ? { config } : {})
  }
}

/**
 * Briqpay hooks are configured per session, so our SF order number rides along in the URL
 * the same way the other providers' push URLs carry it, and they post to the same shared
 * `/api/orders/push`. The body is never read: the endpoint provisions by order number, and
 * `provisionOrderBriqpayProvider` re-reads the session to decide what the hook actually
 * meant — provision, no-op, or (on a rejection) walk the order back to CANCELLED.
 */
const briqpayHooksFactory = (pushDomain: string, orderNumber?: string | null): BriqpayHook[] => {
  const url = resolveUrlPath(
    pushDomain,
    `/api/orders/push?order_id=${encodeURIComponent(orderNumber ?? '')}`
  )

  return [
    { eventType: 'session_status', statuses: ['completed'], method: 'POST', url },
    {
      eventType: 'order_status',
      statuses: [
        'order_pending',
        'order_rejected',
        'order_cancelled',
        'order_approved_not_captured'
      ],
      method: 'POST',
      url
    }
  ]
}

const generateCreateBriqpaySessionPayload = ({
  opts,
  locale,
  items,
  cartDiscountCode,
  cartStateUuid,
  backendOrder,
  countryCode,
  extraCartRows
}: Pick<CreateOrderRequest, 'cartDiscountCode' | 'items' | 'cartStateUuid'> & {
  locale: Language
  opts: CreateOrderHandlerOpts
  backendOrder: { orderNumber?: string | null }
  countryCode: CountryCode | undefined
  extraCartRows?: BriqpayCartItem[]
}): BriqpayCreateSessionRequest => {
  const briqpayOptions = opts.paymentProviderConfig?.providerOptions?.briqpay
  const customerType = resolveBriqpayCustomerType(opts)
  // Only B2B sessions have a module layout to configure — consumer sessions are always
  // payment-only — so the settings are applied for business and skipped otherwise.
  const moduleSettings = customerType === 'business' ? briqpayOptions?.business : undefined

  // VAT stays tied to the merchant's configured default country regardless of the
  // shopper's pick.
  const { orderLines } = briqpayOrderLineFactory(
    items,
    cartDiscountCode,
    briqpayOptions?.countryCode,
    opts.buyerVat
  )
  const cart = [...orderLines, ...(extraCartRows ?? [])]
  // Webhooks must reach a public host; use a mock domain in dev, as Qliro does.
  const pushDomain = IS_PRODUCTION ? opts.redirectDomain : 'https://granditude.com'

  return {
    product: {
      type: 'payment',
      intent: 'payment_one_time',
      ...(briqpayOptions?.variantId ? { variantId: briqpayOptions.variantId } : {})
    },
    customerType,
    country: countryCode || DEFAULT_COUNTRY,
    locale: getBriqpayLocale(locale),
    urls: {
      terms: resolveUrlPath(opts.redirectDomain, briqpayOptions?.termsUrl ?? '/terms'),
      // `urls.redirect` has no template support, the same limitation as Svea's
      // ConfirmationUri — so the SF order number is hardcoded here and the Briqpay
      // sessionId is recovered from the order's persisted `paymentId`.
      redirect: resolveUrlPath(
        opts.redirectDomain,
        `/confirmation?order_id=${encodeURIComponent(backendOrder.orderNumber ?? '')}`
      ),
      ...(briqpayOptions?.withdrawalAndReturnsUrl
        ? {
            withdrawalAndReturns: resolveUrlPath(
              opts.redirectDomain,
              briqpayOptions.withdrawalAndReturnsUrl
            )
          }
        : {})
    },
    references: {
      // The conventional order reference most PSPs behind Briqpay surface.
      reference1: backendOrder.orderNumber ?? '',
      // Replaces Qliro's MerchantProvidedMetadata — the confirmation page matches on it
      // before clearing the cart.
      ...(cartStateUuid ? { cartStateUuid } : {})
    },
    hooks: briqpayHooksFactory(pushDomain, backendOrder.orderNumber),
    config: {
      ...(briqpayOptions?.disableSessionCompleteRedirect
        ? { disableSessionCompleteRedirect: true }
        : {}),
      ...(briqpayOptions?.realTimeProcessing ? { realTimeProcessing: true } : {})
    },
    data: {
      order: briqpayOrderFactory(cart, briqpayOptions?.purchaseCurrency || DEFAULT_CURRENCY)
    },
    modules: briqpayModulesFactory(moduleSettings, briqpayOptions?.decisionEnabled)
  }
}

/**
 * Persist the Briqpay sessionId as the SF order's `paymentId`.
 *
 * Briqpay's `urls.redirect` carries no template variable, so the confirmation page only
 * ever knows the SF order number — the sessionId has to be recoverable from the order
 * itself or the purchase cannot be provisioned. Failing this write aborts the create
 * rather than leaving an order with no link to its payment.
 *
 * `PUT /storefront/orders` is a full replace, so the already-prepared lines are passed
 * back through untouched.
 */
const persistBriqpayPaymentId = async (
  backendOrder: OrderDTO,
  session: BriqpaySession,
  backendItems: CreateOrderItemDTO[]
) => {
  if (!backendOrder.orderNumber) {
    throw new Error('SF order is missing orderNumber — cannot persist Briqpay paymentId')
  }

  const { id: _id, customer: _customer, items: _items, ...rest } = backendOrder

  await storefrontApi.orders.updateStorefrontOrder({
    ...rest,
    items: backendItems,
    paymentId: session.sessionId
  })
}

/**
 * Create the session, link it to the SF order and answer the request. Shared by all four
 * shipping branches — they differ only in what they put in the cart and what they echo
 * back alongside the session.
 */
const createSessionAndRespond = async ({
  res,
  payload,
  backendOrder,
  backendOrderLines,
  extraResponse
}: {
  res: NextApiResponse<CreateOrderResponse | { error: string }>
  payload: BriqpayCreateSessionRequest
  backendOrder: OrderDTO
  backendOrderLines: CreateOrderItemDTO[]
  extraResponse?: Partial<CreateOrderResponse>
}) => {
  const { error, data: session } = await briqpayApi.checkout.createSession(payload)

  if (error || !session) {
    res.status(400).json({ error: error || 'Unknown error' })
    return
  }

  await persistBriqpayPaymentId(backendOrder, session, backendOrderLines)

  res.status(200).json({
    storefrontOrderResponse: backendOrder,
    briqpayResponse: session,
    ...extraResponse
  })
}

/** Everything the branches read out of the request body, parsed once. */
const parseRequest = (req: NextApiRequest, opts: CreateOrderHandlerOpts) => {
  const {
    storeId,
    items,
    cartDiscountCode,
    cartStateUuid,
    countryCode: requestedCountryCode
  }: CreateOrderRequest = JSON.parse(req.body)
  const localeForCredentials = (req.cookies.NEXT_LOCALE as Language) || 'sv'

  return {
    storeId,
    items,
    cartDiscountCode,
    cartStateUuid,
    locale: opts.locale || localeForCredentials,
    countryCode: resolveBriqpayCountry(opts, requestedCountryCode)
  }
}

const simpleShipping = async (
  req: NextApiRequest,
  res: NextApiResponse<CreateOrderResponse | { error: string }>,
  opts: CreateOrderHandlerOpts
) => {
  const { storeId, items, cartDiscountCode, cartStateUuid, locale, countryCode } = parseRequest(
    req,
    opts
  )
  // VAT stays tied to the merchant's configured default country regardless of the
  // shopper's pick.
  const vatCountryCode = opts.paymentProviderConfig?.providerOptions?.briqpay?.countryCode

  const { orderAmount, netOrderAmount } = briqpayOrderLineFactory(
    items,
    cartDiscountCode,
    vatCountryCode,
    opts.buyerVat
  )
  const shippingInfo = paymentShippingFactory({
    orderAmount,
    netOrderAmount,
    locale,
    opts: opts.shippingProviderConfig?.simpleShipping,
    shippingConfig: opts.shippingProviderConfig,
    customerType: opts.customerType,
    // The shopper's country pick drives the rules; VAT stays on the merchant's
    // configured country.
    recipientCountryCode: countryCode,
    countryCode: vatCountryCode,
    buyerVat: opts.buyerVat
  })

  const backendOrderLines = parseStoreToBackendOrderLines(items, cartDiscountCode)
  const backendOrder = await storefrontApi.orders.createStorefrontOrder({
    storeId,
    backendOrderLines,
    cartDiscountCode,
    customerId: opts.customerId,
    paymentProviderInfo: {
      providerName: 'BRIQPAY'
    }
  })

  // Briqpay cannot render a shipping-method list at all — its `shipping` module collects
  // an address, not a rate — so freight always rides along as a `shipping_fee` cart row.
  // A store with several configured methods runs the SPLIT topology and re-pushes this
  // row on every change.
  const payload = generateCreateBriqpaySessionPayload({
    opts,
    backendOrder,
    items,
    cartDiscountCode,
    cartStateUuid,
    locale,
    countryCode,
    extraCartRows: [briqpayShippingLine(shippingInfo)]
  })

  await createSessionAndRespond({ res, payload, backendOrder, backendOrderLines })
}

const postnordShipping = async (
  req: NextApiRequest,
  res: NextApiResponse<CreateOrderResponse | { error: string }>,
  opts: CreateOrderHandlerOpts
) => {
  const { storeId, items, cartDiscountCode, cartStateUuid, locale, countryCode } = parseRequest(
    req,
    opts
  )

  const backendOrderLines = parseStoreToBackendOrderLines(items, cartDiscountCode)
  const backendOrder = await storefrontApi.orders.createStorefrontOrder({
    storeId,
    backendOrderLines,
    cartDiscountCode,
    customerId: opts.customerId,
    paymentProviderInfo: {
      providerName: 'BRIQPAY'
    },
    shipping: {
      provider: 'POSTNORD'
    }
  })

  // The carrier owns the delivery step, so the session starts without a freight row —
  // the FE PATCHes one in once the shopper picks an option.
  const payload = generateCreateBriqpaySessionPayload({
    opts,
    backendOrder,
    items,
    cartDiscountCode,
    cartStateUuid,
    locale,
    countryCode
  })

  await createSessionAndRespond({ res, payload, backendOrder, backendOrderLines })
}

const ingridShipping = (
  req: NextApiRequest,
  res: NextApiResponse<CreateOrderResponse | { error: string }>,
  opts: CreateOrderHandlerOpts
) => {
  void ingridShippingProvider(req, res, opts, async (ingridResponse) => {
    const { storeId, items, cartDiscountCode, cartStateUuid, locale, countryCode } = parseRequest(
      req,
      opts
    )

    const backendOrderLines = parseStoreToBackendOrderLines(items, cartDiscountCode)
    const backendOrder = await storefrontApi.orders.createStorefrontOrder({
      storeId,
      backendOrderLines,
      cartDiscountCode,
      customerId: opts.customerId,
      paymentProviderInfo: {
        providerName: 'BRIQPAY'
      },
      shipping: {
        provider: 'INGRID',
        providerSessionId: ingridResponse.session.checkout_session_id
      }
    })

    const payload = generateCreateBriqpaySessionPayload({
      opts,
      backendOrder,
      items,
      cartDiscountCode,
      cartStateUuid,
      locale,
      countryCode
    })

    await createSessionAndRespond({
      res,
      payload,
      backendOrder,
      backendOrderLines,
      extraResponse: { ingridResponse }
    })
  })
}

const nshiftShipping = async (
  req: NextApiRequest,
  res: NextApiResponse<CreateOrderResponse | { error: string }>,
  opts: CreateOrderHandlerOpts
) => {
  const nshiftResponse = await nshiftShippingProvider(req, opts)

  const { storeId, items, cartDiscountCode, cartStateUuid, locale, countryCode } = parseRequest(
    req,
    opts
  )

  const backendOrderLines = parseStoreToBackendOrderLines(items, cartDiscountCode)
  const backendOrder = await storefrontApi.orders.createStorefrontOrder({
    storeId,
    backendOrderLines,
    cartDiscountCode,
    customerId: opts.customerId,
    paymentProviderInfo: {
      providerName: 'BRIQPAY'
    },
    shipping: {
      provider: 'NSHIFT',
      providerSessionId: nshiftResponse.sessionId
    }
  })

  const payload = generateCreateBriqpaySessionPayload({
    opts,
    backendOrder,
    items,
    cartDiscountCode,
    cartStateUuid,
    locale,
    countryCode
  })

  await createSessionAndRespond({
    res,
    payload,
    backendOrder,
    backendOrderLines,
    extraResponse: { nshiftResponse }
  })
}

export const briqpayPaymentProvider = async (
  req: NextApiRequest,
  res: NextApiResponse,
  opts: CreateOrderHandlerOpts
) => {
  const { shippingProviderConfig } = opts

  switch (shippingProviderConfig?.provider ?? 'DEFAULT') {
    case 'INGRID':
      return ingridShipping(req, res, opts)
    case 'POSTNORD':
      return postnordShipping(req, res, opts)
    case 'NSHIFT':
      return nshiftShipping(req, res, opts)
    case 'DEFAULT':
      return simpleShipping(req, res, opts)
  }
}
